/*
 * Copyright Mimic Networks, Inc. 2026.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError, NodeGroupResponse, PatchNodeGroupSchema } from '@/client';
import { mgmtPlaneAPIClient } from '@/client/mgmtPlane';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export type UpdateNodeGroupVariables = {
  nodeGroupId: string;
  data: PatchNodeGroupSchema;
};

export type UpdateNodeGroupMutationConfig = {
  onSuccess?: (data: NodeGroupResponse, variables: UpdateNodeGroupVariables) => void;
  onError?: (error: unknown, variables: UpdateNodeGroupVariables) => void;
  onSettled?: (data: unknown, error: unknown, variables: unknown) => void;
};

/**
 * Hook for updating an existing node group.
 *
 * @param config.onSuccess - Called after successful node group update
 * @param config.onError - Called if update fails
 * @param config.onSettled - Called after mutation completes (success or failure)
 *
 * @returns React Query mutation result for updating node groups
 */
export function useUpdateNodeGroupMutation(config: UpdateNodeGroupMutationConfig) {
  const queryClient = useQueryClient();
  const tenantId = useMgmtPlaneStateStore((state) => state.selectedTenantID);

  const { mutate, mutateAsync, isError, error, isPending } = useMutation<
    NodeGroupResponse,
    ApiError,
    UpdateNodeGroupVariables
  >({
    mutationFn: ({ nodeGroupId, data }: UpdateNodeGroupVariables) => {
      return mgmtPlaneAPIClient.nodeGroups.updateNodeGroup({
        tenantId: tenantId!,
        nodeGroupId,
        requestBody: data,
      });
    },
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['nodeGroups', tenantId] });
      queryClient.invalidateQueries({ queryKey: ['nodeGroups', tenantId, variables.nodeGroupId] });
      config?.onSuccess?.(data, variables);
    },
    onError: config?.onError,
    onSettled: config?.onSettled,
  });

  return { mutate, mutateAsync, isError, error, isPending };
}
